import React, {
  FC,
  useEffect,
  useState,
} from 'react';
import { Link, NavLink } from 'components';
import { routes } from 'appConstants';
import { _t, lockBodyScroll, openDocumentsModal } from 'utils';

import mospromLogo from 'assets/img/header-v4/mosprom.svg';
import iconSearch from 'assets/img/header-v4/icon-search.svg';
import iconClose from 'assets/img/projects-menu-v4/icon-close.svg';
import iconChevron from 'assets/img/header-v4/icon-chevron.svg';
import iconCtaPlusDark from 'assets/img/header-v4/icon-cta-plus-dark.svg';

import styles from './styles.module.scss';

type MenuLink = {
  title: string;
  to?: string;
  documents?: boolean;
};

type MenuColumn = {
  title: string;
  links: MenuLink[];
};

type MobileItem =
  | { title: string; kind: 'projects' }
  | { title: string; kind: 'link'; to: string };

type Props = {
  onClose: () => void;
  onOpenProjects?: () => void;
  onOpenApplication?: () => void;
};

const COLUMNS: MenuColumn[] = [
  {
    title: 'О компании',
    links: [
      { title: 'Информация', to: routes.about.root },
      { title: 'Новости', to: routes.media.news.root },
      { title: 'Вакансии', to: routes.vacancies.root },
      { title: 'Контакты', to: routes.contacts.root },
    ],
  },
  {
    title: 'Услуги',
    links: [
      { title: 'Аналитическая поддержка', to: `${routes.resolutions.service.root}/analytics` },
      { title: 'Байерская программа', to: `${routes.resolutions.service.root}/buyer` },
      { title: 'Информационно-консультационная поддержка', to: `${routes.resolutions.service.root}/support` },
    ],
  },
  {
    title: 'Сервисы и Данные',
    links: [
      { title: 'Библиотека экспортера', to: routes.resolutions.root },
      { title: 'Тест экспортной готовности', to: routes.auth.exportTest.root },
      { title: 'Официальные документы', documents: true },
    ],
  },
];

const MOBILE_ITEMS: MobileItem[] = [
  { title: 'Проекты', kind: 'projects' },
  { title: 'О нас', kind: 'link', to: routes.about.root },
  { title: 'Экспортерам', kind: 'link', to: routes.resolutions.root },
  { title: 'Аналитика', kind: 'link', to: `${routes.resolutions.service.root}/analytics` },
  { title: 'Новости', kind: 'link', to: routes.media.news.root },
  { title: 'Мероприятия', kind: 'link', to: routes.events.root },
  { title: 'Вакансии', kind: 'link', to: routes.vacancies.root },
  { title: 'Контакты', kind: 'link', to: routes.contacts.root },
];

const MainMenu: FC<Props> = ({
  onClose,
  onOpenProjects,
  onOpenApplication,
}) => {
  const [searchValue, setSearchValue] = useState('');

  useEffect(() => lockBodyScroll(), []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div className={styles.root} role="dialog" aria-modal="true" aria-label={_t('Меню')}>
      <button
        type="button"
        className={styles.backdrop}
        aria-label={_t('Закрыть')}
        onClick={onClose}
      />

      <div className={styles.panel}>
        <div className={styles.top}>
          <div className={styles.topLeft}>
            <Link to={routes.main.root} className={styles.logo} onClick={onClose}>
              <img src={mospromLogo} alt="МОСПРОМ" />
            </Link>
            <div className={styles.search}>
              <label className={styles.srOnly} htmlFor="main-menu-search">
                {_t('Поиск')}
              </label>
              <input
                id="main-menu-search"
                type="search"
                placeholder={_t('Поиск...')}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
              <img src={iconSearch} alt="" className={styles.searchIcon} />
            </div>
          </div>
          <button
            type="button"
            className={styles.close}
            aria-label={_t('Закрыть')}
            onClick={onClose}
          >
            <img src={iconClose} alt="" width={16} height={16} />
          </button>
        </div>

        <div className={styles.columns}>
          {COLUMNS.map((column) => (
            <section key={column.title} className={styles.column}>
              <h3 className={styles.columnTitle}>{_t(column.title)}</h3>
              <ul className={styles.links}>
                {column.links.map((link) => (
                  <li key={`${column.title}-${link.title}`}>
                    {link.documents ? (
                      <button
                        type="button"
                        className={styles.link}
                        onClick={() => {
                          onClose();
                          openDocumentsModal();
                        }}
                      >
                        {`- ${_t(link.title)}`}
                      </button>
                    ) : (
                      <NavLink
                        to={link.to || routes.main.root}
                        className={styles.link}
                        onClick={onClose}
                      >
                        {`- ${_t(link.title)}`}
                      </NavLink>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </div>

      <div className={styles.mobilePanel}>
        <div className={styles.mobileTop}>
          <Link to={routes.main.root} className={styles.mobileLogo} onClick={onClose}>
            <img src={mospromLogo} alt="МОСПРОМ" />
          </Link>
          <button
            type="button"
            className={styles.mobileClose}
            aria-label={_t('Закрыть')}
            onClick={onClose}
          >
            <img src={iconClose} alt="" width={18} height={18} />
          </button>
        </div>

        <nav className={styles.mobileNav} aria-label={_t('Меню')}>
          <ul className={styles.mobileList}>
            {MOBILE_ITEMS.map((item) => (
              <li key={item.title} className={styles.mobileItem}>
                {item.kind === 'projects' ? (
                  <button
                    type="button"
                    className={styles.mobileLinkBtn}
                    onClick={() => {
                      (onOpenProjects ?? onClose)();
                    }}
                  >
                    <span>{_t(item.title)}</span>
                    <img src={iconChevron} alt="" className={styles.mobileChevron} />
                  </button>
                ) : (
                  <NavLink
                    to={item.to}
                    className={styles.mobileLink}
                    onClick={onClose}
                  >
                    {_t(item.title)}
                  </NavLink>
                )}
              </li>
            ))}
          </ul>
        </nav>

        <button
          type="button"
          className={styles.mobileCta}
          onClick={() => {
            (onOpenApplication ?? onClose)();
          }}
        >
          <span className={styles.mobileCtaLabel}>{_t('Подать заявку')}</span>
          <span className={styles.mobileCtaPlus} aria-hidden>
            <img src={iconCtaPlusDark} alt="" width={12} height={12} />
          </span>
        </button>
      </div>
    </div>
  );
};

export default MainMenu;
